import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const LINKS = {
    Services: [
        { to: "/services/corporate-security", label: "Protective Security" },
        { to: "/services/it-cyber", label: "IT & Cyber" },
        { to: "/services/security-consultancy", label: "Security Consultancy" },
        { to: "/services/investigations", label: "Investigations" },
    ],
    Company: [
        { to: "/about", label: "About" },
        { to: "/blog", label: "Insights" },
        { to: "/news", label: "News" },
        { to: "/contact", label: "Contact" },
    ],
    Legal: [
        { to: "/privacy", label: "Privacy Policy" },
        { to: "/cookies", label: "Cookies" },
        { to: "/terms", label: "Terms" },
    ],
};

export default function Footer() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/newsletter", { email });
            toast.success(data.already_subscribed ? "You're already subscribed." : "Thanks, you're in.");
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="relative bg-dr-navy text-white dr-grain overflow-hidden">
            {/* Big CTA */}
            <div className="dr-container pt-28 pb-16 border-b border-white/10">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <p className="text-xs tracking-[0.32em] text-dr-blue uppercase font-medium">Let&apos;s talk</p>
                        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] mt-6 max-w-[14ch]">
                            Helping you reduce your personal, or organisations risk foot print?
                        </h2>
                        <p className="mt-6 text-white/70 text-lg max-w-xl">
            Book a confidential consultation with a senior consultant. No pitch, no obligation, just a
                            straightforward conversation about your risk landscape.
                        </p>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end">
                        <Link
                            to="/contact"
                            data-testid="footer-cta-book"
                            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-dr-blue text-dr-navy font-semibold hover:bg-white transition-colors"
                        >
                            Book consultation
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Newsletter */}
            <div className="dr-container py-14 border-b border-white/10">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <h3 className="font-display text-2xl font-semibold">Quarterly briefing</h3>
                        <p className="text-white/70 mt-2 max-w-md">
                            Analysis, case notes and regulatory shifts for boards and risk leaders. Four times a year, never more.
                        </p>
                    </div>
                    <form onSubmit={submit} data-testid="newsletter-form" className="flex gap-2">
                        <input
                            data-testid="newsletter-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com"
                            className="flex-1 px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-dr-blue"
                        />
                        <button
                            data-testid="newsletter-submit-btn"
                            disabled={submitting}
                            className="px-6 py-3.5 rounded-full bg-white text-dr-navy font-semibold hover:bg-dr-blue disabled:opacity-60 transition-colors"
                        >
                            {submitting ? "…" : "Subscribe"}
                        </button>
                    </form>
                </div>
            </div>

            {/* Link columns */}
            <div className="dr-container py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
                <div className="col-span-2">
                    <img
                        src="/logo-white.png"
                        alt="Dynamic Risks — Assess. Adapt. Protect."
                        className="h-40 w-40 object-contain"
                    />
                    <p className="text-white/60 text-sm mt-5 max-w-xs leading-relaxed">
                        A UK risk management consultancy providing specialist advice in corporate security,
                        investigations, cyber resilience and security consultancy.
                    </p>
                </div>
                {Object.entries(LINKS).map(([heading, items]) => (
                    <div key={heading}>
                        <p className="text-xs uppercase tracking-[0.28em] text-dr-blue/90 font-medium">{heading}</p>
                        <ul className="mt-5 space-y-3">
                            {items.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="text-sm text-white/80 hover:text-dr-blue transition-colors"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            <div className="dr-container py-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <p className="text-xs text-white/50">© {new Date().getFullYear()} Dynamic Risks Ltd. All rights reserved.</p>
                <p className="text-xs text-white/50">Registered in England &amp; Wales.</p>
            </div>
        </footer>
    );
}
